import axios from 'axios';
import { serverConfig } from './server-config';

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // Auth token has expired or is invalid, delete it from local storage
            localStorage.removeItem('authToken');
            location.reload();
        }
        return Promise.reject(error);
    }
);

export const BolnaService = {
    /**
     * Initiate an AI call to a candidate
     * @param {string} candidateId - The candidate ID
     * @param {string} candidateQuestions - Questions for the candidate (comma-separated or newline-separated)
     * @param {string} evaluationCriteria - Evaluation criteria for the call
     * @param {string} [scheduledAt] - Optional scheduled time in ISO format (e.g., "2025-08-21T10:35:00")
     * @returns {Promise} - Returns { message, status, execution_id }
     */
    initiateCall: async (candidateId, candidateQuestions, questionEvaluations, scheduledAt = null, clientId = null) => {
        const payload = {
            candidate_id: candidateId,
            candidate_questions: candidateQuestions,
            question_evaluations: questionEvaluations,
        };

        if (scheduledAt) {
            payload.scheduled_at = scheduledAt;
        }

        if (clientId) {
            payload.client_id = clientId;
        }

        return axios.post(
            `https://${serverConfig.serverBaseAddress}/signite/bolna/initiate-call`,
            payload,
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                    'Content-Type': 'application/json',
                },
            }
        );
    },

    /**
     * Get the status of a call by execution ID
     * @param {string} executionId - The execution ID returned from initiateCall
     * @returns {Promise} - Returns call status details including transcript, summary, recording URL, etc.
     */
    getCallStatus: async (executionId) => {
        return axios.get(
            `https://${serverConfig.serverBaseAddress}/signite/bolna/call/status?executionId=${executionId}`,
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                },
            }
        );
    },

    /**
     * Get all calls for a candidate
     * @param {string} candidateId - The candidate ID
     * @returns {Promise} - Returns array of call records for the candidate
     */
    getCalls: async (candidateId) => {
        return axios.get(
            `https://${serverConfig.serverBaseAddress}/signite/bolna/calls?candidateId=${candidateId}`,
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                },
            }
        );
    },

    /**
     * Refresh and get the latest status of a call by execution ID
     * This calls the Bolna API to get the latest status and updates the database
     * @param {string} executionId - The execution ID returned from initiateCall
     * @returns {Promise} - Returns updated call status details including transcript, summary, recording URL, etc.
     */
    refreshCallStatus: async (executionId) => {
        return axios.get(
            `https://${serverConfig.serverBaseAddress}/signite/bolna/call/refresh?executionId=${executionId}`,
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                    'Content-Type': 'application/json',
                },
            }
        );
    },

    /**
     * Download a call recording
     * @param {string} recordingPath - The path to the recording file
     * @returns {Promise} - Returns blob data of the recording
     */
    getCallRecording: (recordingPath) => {
        return axios({
            method: 'get',
            url: `https://${serverConfig.serverBaseAddress}/signite/file/download?path=${recordingPath}`,
            responseType: 'blob',
            headers: {
                Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            },
        });
    },

    /**
     * Sync/resync recording for a call
     * @param {string} executionId - The execution ID of the call
     * @returns {Promise} - Returns updated call data with recording info
     */
    syncRecording: async (executionId) => {
        return axios.post(
            `https://${serverConfig.serverBaseAddress}/signite/bolna/call/sync-recording?executionId=${executionId}`,
            {},
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                    'Content-Type': 'application/json',
                },
            }
        );
    },

    /**
     * Get candidate profile with AI call score
     * @param {string} candidateId - The candidate ID
     * @param {number} page - Page number (default 0)
     * @param {number} size - Page size (default 10)
     * @returns {Promise} - Returns candidate profile with latest call score and history
     */
    getCandidateProfile: async (candidateId, page = 0, size = 10) => {
        return axios.get(
            `https://${serverConfig.serverBaseAddress}/signite/bolna/candidate-profile?candidateId=${candidateId}&page=${page}&size=${size}`,
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                    'Content-Type': 'application/json',
                },
            }
        );
    },

    /**
    * Create a question template
    * @param {string} name
    * @param {Array} questions
    * @param {string|null} clientId
    * @returns {Promise}
    */
    createQuestionTemplate: async (name, questions, clientId = null) => {
        const url = clientId
            ? `https://${serverConfig.serverBaseAddress}/signite/question-template?clientId=${clientId}`
            : `https://${serverConfig.serverBaseAddress}/signite/question-template`;

        return axios.post(
            url,
            {
                name,
                questions
            },
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                    'Content-Type': 'application/json',
                },
            }
        );
    },

    /**
     * Get all question templates
     * @returns {Promise}
     */
    getQuestionTemplates: async (clientId = null) => {
        const url = clientId
            ? `https://${serverConfig.serverBaseAddress}/signite/question-templates?clientId=${clientId}`
            : `https://${serverConfig.serverBaseAddress}/signite/question-templates`;
        return axios.get(
            url,
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                },
            }
        );
    },

    /**
     * Delete a question template by ID
     * @param {string} id - Template ID
     * @returns {Promise}
     */
    deleteQuestionTemplate: async (id) => {
        return axios.delete(
            `https://${serverConfig.serverBaseAddress}/signite/question-template/${id}`,
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                },
            }
        );
    },
    updateQuestionTemplate: async (id, name, questions) => {
        return axios.post(
            `https://${serverConfig.serverBaseAddress}/signite/question-template/update`,
            {
                id,
                name,
                questions
            },
            {
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
                    'Content-Type': 'application/json',
                },
            }
        );
    },
};
